import { useEffect, useRef, useState } from "react";

import ThumbImg from "../../assets/images/conversations/blank.png";
import PlayIcon  from "../../assets/icons/conversations/play-icon.svg";

/* ─────────────────────────────────────────────
   PREFIX: gn2-
───────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Roboto:wght@400;500&display=swap');

  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

  .gn2-page {
    background: #1A1A1A;
    min-height: 100vh;
    font-family: 'Roboto', sans-serif;
    color: #fff;
  }
  .gn2-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
  }

  /* ── HERO ── */
  .gn2-hero { padding-top: 60px; padding-bottom: 60px; }
  .gn2-hero__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    align-items: start;
  }
  .gn2-hero__left { display: flex; flex-direction: column; gap: 44px; }
  .gn2-hero__title {
    font-family: 'Montserrat', sans-serif;
    font-size: 32px;
    font-weight: 700;
    color: #ffffff;
    line-height: 140%;
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .gn2-hero__title.gn2-visible { opacity: 1; transform: translateY(0); }
  .gn2-hero__spotify {
    border-radius: 12px;
    overflow: hidden;
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease 0.18s, transform 0.7s ease 0.18s;
  }
  .gn2-hero__spotify.gn2-visible { opacity: 1; transform: translateY(0); }
  .gn2-hero__spotify iframe {
    display: block; width: 100%; height: 152px; border: none; border-radius: 12px;
  }
  .gn2-hero__right {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s;
  }
  .gn2-hero__right.gn2-visible { opacity: 1; transform: translateY(0); }

  /* ── YOUTUBE THUMB ── */
  .gn2-yt-thumb {
    position: relative; width: 100%;
    border-radius: 12px; overflow: hidden;
    cursor: pointer; display: block; background: #000;
  }
  .gn2-yt-thumb img {
    width: 100%; height: auto; display: block;
    border-radius: 12px; transition: opacity 0.3s ease;
  }
  .gn2-yt-thumb:hover img { opacity: 0.85; }
  .gn2-yt-thumb__play {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    background: none; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    pointer-events: none;
  }
  .gn2-yt-thumb__play img { width: 40px; height: 44px; }
  .gn2-yt-iframe-wrap {
    position: relative; width: 100%; padding-bottom: 56.25%;
    height: 0; border-radius: 12px; overflow: hidden;
  }
  .gn2-yt-iframe-wrap iframe {
    position: absolute; top: 0; left: 0;
    width: 100%; height: 100%; border: none;
  }

  /* ── BODY ── */
  .gn2-body {
    padding-bottom: 100px; opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .gn2-body.gn2-visible { opacity: 1; transform: translateY(0); }
  .gn2-body__para {
    font-family: 'Roboto', sans-serif; font-size: 16px;
    font-weight: 400; color: #E2E2E2; line-height: 160%; margin-bottom: 14px;
  }
  .gn2-body__para:last-child { margin-bottom: 0; }

  /* ── TABLET (max 768px) ── */
  @media (max-width: 768px) {
    .gn2-inner { padding-left: 32px; padding-right: 32px; }
    .gn2-hero { padding-top: 32px; padding-bottom: 48px; }
    .gn2-hero__grid { grid-template-columns: 1fr 1fr; gap: 24px; }
    .gn2-hero__title { font-size: 24px; }
    .gn2-hero__left { gap: 16px; }
    .gn2-body__para { font-size: 14px; margin-bottom: 12px; }
    .gn2-body { padding-bottom: 48px; }
    .gn2-yt-thumb__play img { width: 30px; height: 32px; }
  }

  /* ── MOBILE (max 480px) ── */
  @media (max-width: 480px) {
    .gn2-inner { padding-left: 16px; padding-right: 16px; }
    .gn2-hero { padding-top: 51px; padding-bottom: 30px; }
    .gn2-hero__grid { grid-template-columns: 1fr; gap: 30px; }
    .gn2-hero__left { gap: 30px; }
    .gn2-hero__title { font-size: 30px; }
    .gn2-body__para { font-size: 16px; margin-bottom: 10px; }
    .gn2-body { padding-bottom: 30px; }
    .gn2-yt-thumb__play img { width: 30px; height: 32px; }
  }
`;

function useFadeUp(ref) {
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("gn2-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
}

const YT_VIDEO_ID = "4Rq4dJkr7j4";

function YouTubeEmbed() {
  const [playing, setPlaying] = useState(false);
  if (playing) {
    return (
      <div className="gn2-yt-iframe-wrap">
        <iframe
          src={`https://www.youtube.com/embed/${YT_VIDEO_ID}?autoplay=1&rel=0`}
          title="EP 22 Sunfuel Electric Gul Panag Sudhir Nayak"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <div
      className="gn2-yt-thumb"
      onClick={() => setPlaying(true)}
      role="button"
      aria-label="Play video"
    >
      <img src={ThumbImg} alt="EP 22 Sunfuel Electric thumbnail" />
      <div className="gn2-yt-thumb__play">
        <img src={PlayIcon} alt="Play" />
      </div>
    </div>
  );
}

function HeroSection() {
  const titleRef   = useRef(null);
  const spotifyRef = useRef(null);
  const rightRef   = useRef(null);
  useFadeUp(titleRef);
  useFadeUp(spotifyRef);
  useFadeUp(rightRef);

  return (
    <section className="gn2-hero">
      <div className="gn2-inner">
        <div className="gn2-hero__grid">
          <div className="gn2-hero__left">
            <h1 ref={titleRef} className="gn2-hero__title">
              EP 22 | Making EVs Mainstream | Sunfuel Electric | Gul PANAG &amp; Sudhir NAYAK
            </h1>
            <div ref={spotifyRef} className="gn2-hero__spotify">
              <iframe
                src="https://open.spotify.com/embed/episode/1uJa4fAYvGNDtXPWMcPxgT?utm_source=generator&theme=0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                title="EP 22 Spotify Player"
              />
            </div>
          </div>
          <div ref={rightRef} className="gn2-hero__right">
            <YouTubeEmbed />
          </div>
        </div>
      </div>
    </section>
  );
}

function BodySection() {
  const ref = useRef(null);
  useFadeUp(ref);
  return (
    <section ref={ref} className="gn2-body">
      <div className="gn2-inner">
        <p className="gn2-body__para">
          In this episode we go back to the charging infrastructure sector and speak to a unique new startup charge point operator co-founded by a team of people who bring a myriad of experience from the consulting, advertising, manufacturing and even the film industry. The one thing that this team has in common is a passion for clean mobility and cleaner air in our cities.
        </p>
        <p className="gn2-body__para">
          Their operating model for charging infrastructure is different as well, as they use their chargers as outdoor digital advertising opportunities, generating alternative revenue streams for the business. We speak today with two of the co-founders of Sunfuel Electric, Gul Panag and Sudhir Nayak who together with their team through in-depth usage accessibility and ergonomics research have designed what could become one of the most unique chargers designs in the country.
        </p>
      </div>
    </section>
  );
}

export default function Ep22GulPanagSudhirNayak() {
  return (
    <>
      <style>{CSS}</style>
      <div className="gn2-page">
        <HeroSection />
        <BodySection />
      </div>
    </>
  );
}